import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Divider, Button, useTheme } from "@mui/material";
import { styled } from '@mui/system';
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.mode === 'dark' ? '#1F2A40' : '',
  boxShadow: theme.shadows[3],
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  fontWeight: 'bold',
  fontSize: '1rem',
}));

const EmployeePayroll = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [payroll, setPayroll] = useState(null);
  const navigate = useNavigate();
  const accessToken = JSON.parse(localStorage.getItem('emsUser'))?.user?.stsTokenManager?.accessToken;

  useEffect(() => {
    const userEmail = localStorage.getItem("UserEmail");

    if (!userEmail) {
      console.error("No user email found in localStorage");
      return;
    }

    fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}/payroll-specific`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ email: userEmail }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPayroll(data);
      })
      .catch((error) => console.error("Error fetching payroll data:", error));
  }, []);

  if (!payroll) return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: colors.primary[900],
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        backdropFilter: "blur(5px)"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress sx={{ color: colors.grey[100] }} />
      </Box>
    </Box>
  );

  const { first_name, last_name, Email, Payroll_Details } = payroll;
  const earnings = Payroll_Details.earnings;
  const deductions = Payroll_Details.deductions;

  const totalEarnings = Object.values(earnings).reduce((a, b) => a + Number(b), 0);
  const totalDeductions = Object.values(deductions).reduce((a, b) => a + Number(b), 0);
  const netSalary = totalEarnings - totalDeductions;

  const downloadCSV = () => {
    const rows = [
      ["Category", "Component", "Amount (₹)"],
      ...Object.entries(earnings).map(([key, value]) => ["Earnings", key, value]),
      ...Object.entries(deductions).map(([key, value]) => ["Deductions", key, value]),
      ["", "Current Salary", netSalary],
    ];

    const csvContent = rows.map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payroll.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadPayslip = () => {
    const emailId = localStorage.getItem("UserEmail");
    navigate(`/employee/${emailId}/employeepayslip`);
  };

  return (
    <Box
      sx={{
        maxWidth: "900px",
        margin: "20px auto",
        padding: theme.spacing(3),
      }}
    >
      <StyledPaper elevation={3}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            textAlign: "center",
            color: colors.greenAccent[500],
            mb: 2,
          }}
        >
          Employee Payroll Details
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "600",
            textAlign: "center",
            color: colors.grey[100],
            mb: 1,
          }}
        >
          {first_name} {last_name}
        </Typography>
        <Typography
          sx={{
            textAlign: "center",
            color: colors.grey[300],
            mb: 3,
          }}
        >
          {Email}
        </Typography>

        <Box
          sx={{
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
            textAlign: "center",
            mb: 3,
            backgroundColor: theme.palette.mode === 'dark' ? '' : '#f5f5f5',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
            }}
          >
            Net Salary: ₹{netSalary}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 3 }}>
          <Box sx={{ width: "48%" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                textAlign: "center",
                color: colors.greenAccent[500],
                mb: 2,
              }}
            >
              Earnings
            </Typography>
            <StyledPaper elevation={3}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box
                      component="th"
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        padding: theme.spacing(1.5),
                        textAlign: "center",
                      }}
                    >
                      Component
                    </Box>
                    <Box
                      component="th"
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        padding: theme.spacing(1.5),
                        textAlign: "center",
                      }}
                    >
                      Amount (₹)
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {Object.entries(earnings).map(([key, value]) => (
                    <Box
                      component="tr"
                      key={key}
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.primary[800],
                        },
                      }}
                    >
                      <Box
                        component="td"
                        sx={{
                          padding: theme.spacing(1.5),
                          borderBottom: `1px solid ${colors.grey[700]}`,
                          textAlign: "center",
                          color: colors.grey[100],
                        }}
                      >
                        {key}
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          padding: theme.spacing(1.5),
                          borderBottom: `1px solid ${colors.grey[700]}`,
                          textAlign: "center",
                          color: colors.grey[100],
                        }}
                      >
                        {value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </StyledPaper>
          </Box>

          <Box sx={{ width: "48%" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                textAlign: "center",
                color: colors.greenAccent[500],
                mb: 2,
              }}
            >
              Deductions
            </Typography>
            <StyledPaper elevation={3}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box
                      component="th"
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        padding: theme.spacing(1.5),
                        textAlign: "center",
                      }}
                    >
                      Component
                    </Box>
                    <Box
                      component="th"
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                        padding: theme.spacing(1.5),
                        textAlign: "center",
                      }}
                    >
                      Amount (₹)
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {Object.entries(deductions).map(([key, value]) => (
                    <Box
                      component="tr"
                      key={key}
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.primary[800],
                        },
                      }}
                    >
                      <Box
                        component="td"
                        sx={{
                          padding: theme.spacing(1.5),
                          borderBottom: `1px solid ${colors.grey[700]}`,
                          textAlign: "center",
                          color: colors.grey[100],
                        }}
                      >
                        {key}
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          padding: theme.spacing(1.5),
                          borderBottom: `1px solid ${colors.grey[700]}`,
                          textAlign: "center",
                          color: colors.grey[100],
                        }}
                      >
                        {value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </StyledPaper>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <StyledButton
            variant="contained"
            sx={{
              backgroundColor: colors.greenAccent[700],
              color: colors.grey[100],
              '&:hover': {
                backgroundColor: colors.greenAccent[800],
              },
              flex: 1,
            }}
            onClick={downloadCSV}
          >
            Download CSV
          </StyledButton>
          <StyledButton
            variant="contained"
            sx={{
              backgroundColor: colors.greenAccent[700],
              color: colors.grey[100],
              '&:hover': {
                backgroundColor: colors.greenAccent[800],
              },
              flex: 1,
            }}
            onClick={handleDownloadPayslip}
          >
            Download Payslip
          </StyledButton>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default EmployeePayroll;